import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car, Settings, Fuel, Gauge, Wind } from "lucide-react";

export default function CarDetailsForm({ carData, carDetails, setCarDetails, onChange, hideTurbo = false }) {
  const setter = onChange || setCarDetails;
  const update = (key, val) => setter(prev => ({ ...prev, [key]: val }));

  const models = carData?.models_by_make?.[carDetails.make] || [];

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden">
      <div className="border-b border-slate-200 p-4 bg-slate-50/50 flex items-center gap-2">
        <Car size={18} className="text-slate-500" />
        <span className="text-sm font-bold uppercase tracking-wider text-slate-500">Vehicle Details</span>
      </div>
      <CardContent className="p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Make */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Make</Label>
            <Select value={carDetails.make} onValueChange={(v) => { update("make", v); update("model", ""); }}>
              <SelectTrigger className="bg-slate-50 border-slate-200 rounded-sm font-mono text-base h-12" data-testid="select-make">
                <SelectValue placeholder="Select make" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {(carData?.makes || []).map(m => (
                  <SelectItem key={m} value={m} className="text-base">{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Model */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Model</Label>
            <Select value={carDetails.model} onValueChange={(v) => update("model", v)} disabled={!carDetails.make}>
              <SelectTrigger className="bg-slate-50 border-slate-200 rounded-sm font-mono text-base h-12" data-testid="select-model">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {models.map(m => (
                  <SelectItem key={m} value={m} className="text-base">{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Year */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Year of Mfg</Label>
            <Select value={carDetails.year?.toString() || ""} onValueChange={(v) => update("year", parseInt(v))}>
              <SelectTrigger className="bg-slate-50 border-slate-200 rounded-sm font-mono text-base h-12" data-testid="select-year">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {(carData?.years || []).map(y => (
                  <SelectItem key={y} value={y.toString()} className="text-base">{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Transmission */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <Settings size={12} /> Transmission
            </Label>
            <Select value={carDetails.transmission} onValueChange={(v) => update("transmission", v)}>
              <SelectTrigger className="bg-slate-50 border-slate-200 rounded-sm font-mono text-base h-12" data-testid="select-transmission">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {(carData?.transmissions || []).map(t => (
                  <SelectItem key={t} value={t} className="text-base">{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fuel Type */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <Fuel size={12} /> Fuel Type
            </Label>
            <Select value={carDetails.fuel_type} onValueChange={(v) => update("fuel_type", v)}>
              <SelectTrigger className="bg-slate-50 border-slate-200 rounded-sm font-mono text-base h-12" data-testid="select-fuel">
                <SelectValue placeholder="Select fuel" />
              </SelectTrigger>
              <SelectContent>
                {(carData?.fuel_types || []).map(f => (
                  <SelectItem key={f} value={f} className="text-base">{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* KM Driven */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <Gauge size={12} /> KM Driven
            </Label>
            <Input
              type="number"
              placeholder="e.g. 45000"
              value={carDetails.km_driven}
              onChange={(e) => update("km_driven", parseInt(e.target.value) || "")}
              className="bg-slate-50 border-slate-200 rounded-sm font-mono text-base h-12"
              data-testid="input-km"
            />
          </div>
        </div>

        {/* Turbo Option - MANDATORY (hidden in MFCWL mode) */}
        {!hideTurbo && (
        <div className="mt-5 pt-5 border-t border-slate-200">
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <Wind size={12} /> Turbo/Supercharger <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={carDetails.has_turbo === true ? "yes" : carDetails.has_turbo === false ? "no" : ""} 
              onValueChange={(v) => update("has_turbo", v === "yes")}
            >
              <SelectTrigger 
                className={`bg-slate-50 border-slate-200 rounded-sm font-mono text-base h-12 ${
                  carDetails.has_turbo === undefined || carDetails.has_turbo === null ? 'border-red-300' : ''
                }`}
                data-testid="select-turbo"
              >
                <SelectValue placeholder="Select turbo option *" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no" className="text-base">No - Naturally Aspirated</SelectItem>
                <SelectItem value="yes" className="text-base">Yes - Turbocharged/Supercharged</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-400">
              Select whether your vehicle has a turbocharger or supercharger
            </p>
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  );
}
